import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Slider,
    FormControlLabel,
    Checkbox,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';

const DigestionLog = () => {
    const [log, setLog] = useState({
        stool_consistency: '',
        stool_color: '',
        stool_odor: '',
        bloating: false,
        gas: false,
        acidity: false,
        abdominal_pain: false,
        comfort_level: 5,
        notes: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchTodayLog();
    }, []);

    const fetchTodayLog = async () => {
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

            const response = await fetch(`${API_BASE_URL}/digestion/today`, { headers });
            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setLog(data);
                }
            }
        } catch (err) {
            console.error('Error fetching digestion log:', err);
        }
    };

    const handleChange = (field, value) => {
        setLog(prev => ({ ...prev, [field]: value }));
        setSuccess(false);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await fetch(`${API_BASE_URL}/digestion/log`, {
                method: 'POST',
                headers,
                body: JSON.stringify(log)
            });

            if (!response.ok) throw new Error('Error al guardar');

            setSuccess(true);
        } catch (err) {
            setError('No se pudo guardar el registro.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={700}>
                Registro de Digestión (Diario)
            </Typography>

            {success && <Alert severity="success" sx={{ mb: 2 }}>Guardado correctamente</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Consistencia</InputLabel>
                        <Select
                            value={log.stool_consistency || ''}
                            label="Consistencia"
                            onChange={(e) => handleChange('stool_consistency', e.target.value)}
                        >
                            <MenuItem value="liquid">Líquida</MenuItem>
                            <MenuItem value="soft">Blanda</MenuItem>
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="hard">Dura</MenuItem>
                            <MenuItem value="pellets">Bolitas</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Color</InputLabel>
                        <Select
                            value={log.stool_color || ''}
                            label="Color"
                            onChange={(e) => handleChange('stool_color', e.target.value)}
                        >
                            <MenuItem value="brown">Marrón</MenuItem>
                            <MenuItem value="bronze">Bronce (Ideal)</MenuItem>
                            <MenuItem value="green">Verde</MenuItem>
                            <MenuItem value="yellow">Amarillo</MenuItem>
                            <MenuItem value="black">Negro</MenuItem>
                            <MenuItem value="red">Rojo</MenuItem>
                            <MenuItem value="pale">Pálido</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Typography gutterBottom variant="subtitle2">Síntomas</Typography>
                    <Grid container>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel
                                control={<Checkbox checked={log.bloating} onChange={(e) => handleChange('bloating', e.target.checked)} />}
                                label="Hinchazón"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel
                                control={<Checkbox checked={log.gas} onChange={(e) => handleChange('gas', e.target.checked)} />}
                                label="Gases"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel
                                control={<Checkbox checked={log.acidity} onChange={(e) => handleChange('acidity', e.target.checked)} />}
                                label="Acidez"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel
                                control={<Checkbox checked={log.abdominal_pain} onChange={(e) => handleChange('abdominal_pain', e.target.checked)} />}
                                label="Dolor"
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Typography gutterBottom variant="subtitle2">Nivel de Confort (1-10)</Typography>
                    <Slider
                        value={log.comfort_level}
                        onChange={(e, val) => handleChange('comfort_level', val)}
                        step={1}
                        marks
                        min={1}
                        max={10}
                        valueLabelDisplay="auto"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Notas adicionales"
                        value={log.notes || ''}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        size="small"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSubmit}
                        disabled={loading}
                        fullWidth
                    >
                        {loading ? 'Guardando...' : 'Guardar Registro'}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default DigestionLog;
